// Libraries
import React, { Fragment, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
// Components
import InsuranceEligibility from './InsuranceEligibility';
import { EligibilityCheckModal } from './EligibilityCheckModal';
// Services
import { getCheckEligibilityRequests, getMostRecentEligibilityChecks } from '../../../services/eligibilityChecker';
// Actions
import { UpdatePatient } from '../../../actions/patient';
import ShowNotification from '../../../actions/notification';
// Constants
import { BRIDGE_SERVICE_NAMES, EMPTY_STRING, NOTIFICATION_TYPE } from '../../../constants/constants';

export function EligibilityCheck() {
  const {
    patient: {
      nonUniquePatientIdentifications = [],
      insuranceEligibility = [
        { serviceType: BRIDGE_SERVICE_NAMES.CCM.id }, { serviceType: BRIDGE_SERVICE_NAMES.FOLLOW_UP.id },
      ],
    } = {},
    tenant: { isReadOnly = false, isBridgeEligibilityCheckerDisplayed = false } = {},
  } = useSelector(state => state);
  const { id: patientId } = useParams();

  const dispatch = useDispatch();
  const updatePatient = data => dispatch(UpdatePatient(data));
  const showNotification = data => dispatch(ShowNotification(data));

  const [isCheckEligibilityModalOpen, setIsCheckEligibilityModalOpen] = useState(false);
  const [lastCheckDate, setLastCheckDate] = useState(EMPTY_STRING);
  const [pendingEligibilityRequestIds, setPendingEligibilityRequestIds] = useState([]);

  const { eligibilityResult, eligibilityStatus } = useQuery({
    queryKey: ['eligibilityCheckPoll', pendingEligibilityRequestIds],
    queryFn: () => pollEligibility(),
    retry: 20,
    enabled: pendingEligibilityRequestIds && pendingEligibilityRequestIds.length > 0,
    refetchOnWindowFocus: false,
    retryDelay: (attemptIndex) => {
      const baseDelay = Math.min(500 * Math.pow(2, attemptIndex), 10000);
      const jitter = Math.random() * 500;
      return baseDelay + jitter;
    }
  });

  const showEligibilityCheck = () => {
    // Verify that bridge eligibility is enabled and the patient contains a bridge patient id
    return isBridgeEligibilityCheckerDisplayed
      && nonUniquePatientIdentifications
        .some(nonUniquePatientIdentification => nonUniquePatientIdentification.root === "bridge_patient_id");
  };

  const getEligibilityCheckDate = (dateString) => {
    if (!dateString.endsWith('Z')) {
      dateString = `${dateString}Z`;
    }
    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const updateEligibilityData = (data) => {
    if (data) {
      if (data.dateOfService) {
        setLastCheckDate(getEligibilityCheckDate(data.dateOfService));
      }
      if (data.recentEligibilityRequests) {
        updatePatient({
          insuranceEligibility: data.recentEligibilityRequests,
        });
      }
    }
  };

  const pollEligibility = async () => {
    if (pendingEligibilityRequestIds && pendingEligibilityRequestIds.length > 0) {
      const getCheckEligibilityRequest = getCheckEligibilityRequests(patientId, pendingEligibilityRequestIds);
      const result = await getCheckEligibilityRequest.promise;

      const stillPending = result?.recentEligibilityRequests?.some(eligibility => eligibility.status?.toLowerCase() === 'pending');
      if (stillPending) {
        throw new Error('Eligibility checks are pending...');
      }

      updateEligibilityData(result);

      return result;
    }
    return {};
  };

  const handleCheckEligibilityClick = () => {
    updatePatient({insuranceEligibility});
    setIsCheckEligibilityModalOpen(true);
  };

  useEffect(() => {
    if (showEligibilityCheck() && patientId) {
      // Get most recent eligibility checkEligibility info
      const getMostRecentRequest = getMostRecentEligibilityChecks(patientId);
      const getMostRecentPromise = getMostRecentRequest.promise;

      getMostRecentPromise.then(mostRecent => {
        delete getMostRecentRequest.promise;
        updateEligibilityData(mostRecent);
      }).catch((error) => {
        delete getMostRecentRequest.promise;
        if (error.status === 401 || error.status === 403 || error.isCanceled) {
          return;
        }
        showNotification({
          message: 'Could not load most recent eligibility check data.',
          autoHide: true,
          notificationType: NOTIFICATION_TYPE.ERROR,
        });
      });
    }
  }, [patientId]);

  useEffect(() => {
    if (eligibilityStatus === 'success' && eligibilityResult) {
      updatePatient({
        insuranceEligibility: eligibilityResult.recentEligibilityRequests,
      });
    }
  }, [eligibilityResult, eligibilityStatus]);

  useEffect(() => {
    const stillPending = insuranceEligibility.some(eligibility => eligibility.status?.toLowerCase() === 'pending');
    const pendingRequestIds = stillPending ? insuranceEligibility
      .filter(eligibility => eligibility.requestId) // Filter requests that don't have a request ID yet
      .map(eligibility => eligibility.requestId) : [];

    const requestIdsUpdated = !pendingRequestIds.every(((requestId, index) => requestId === pendingEligibilityRequestIds[index]));

    if (requestIdsUpdated) {
      setPendingEligibilityRequestIds(pendingRequestIds);
    }
  }, [insuranceEligibility]);

  return (
    <Fragment>
      {showEligibilityCheck()
        && (
          <div className="box-wrapper patient-eligibility-checker-info my-2 p-2 col-12 text-left">
            <div className="d-flex justify-content-between">
              <div>
                <span className="mr-2">Eligibility Check Required:</span>
                <span data-test="summary_patientEligibilityCheckRequired" className="mr-2">Yes</span>
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <div>
                <span className="mr-2">Check Performed:</span>
                <span data-test="summary_patientEligibilityCheckDate" className="mr-2">
                    {lastCheckDate}
                  </span>
              </div>
              <div>
                <Button
                  size='sm'
                  className='mr-2'
                  variant="primary"
                  onClick={handleCheckEligibilityClick}
                  data-test="checkEligibility_openModalButton"
                  disabled={isReadOnly}
                >
                  <i className="mr-2 bi-check-lg" />
                  Check Eligibility
                </Button>
              </div>
            </div>
            <div>Amounts displayed are patient estimates only:</div>
            <InsuranceEligibility data-test="summary_patientEligibility" services={insuranceEligibility} />

            <EligibilityCheckModal
              isModalOpen={isCheckEligibilityModalOpen}
              setIsModalOpen={setIsCheckEligibilityModalOpen}
              patientId={patientId}
              data-test="summary_checkEligibilityModal"
            />
          </div>
        )
      }
    </Fragment>
  );
}

export default EligibilityCheck;
